
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

interface FormData {
  name: string;
  role: string;
  institution: string;
  bio: string;
  website: string;
  twitter: string;
  linkedin: string;
  orcid: string;
}

interface ProfileFormProps {
  email: string | undefined;
  formData: FormData;
  isEditing: boolean;
  isSaving: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSave: () => void;
  onCancel: () => void;
  onEdit: () => void;
  onSignOut: () => void;
}

const ProfileForm = ({
  email,
  formData,
  isEditing,
  isSaving,
  onInputChange,
  onSave,
  onCancel,
  onEdit,
  onSignOut,
}: ProfileFormProps) => {
  return (
    <Card className="md:col-span-2">
      <CardHeader>
        <CardTitle>Profile Information</CardTitle>
        <CardDescription>
          Update your personal information
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              value={email || ""}
              disabled
              className="bg-muted/50"
            />
            <p className="text-xs text-muted-foreground">
              Your email address cannot be changed
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={onInputChange}
              disabled={!isEditing}
              className={!isEditing ? "bg-muted/50" : ""}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Academic Role</Label>
            <Input
              id="role"
              name="role"
              value={formData.role}
              onChange={onInputChange}
              disabled={!isEditing}
              className={!isEditing ? "bg-muted/50" : ""}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="institution">Institution</Label>
            <Input
              id="institution"
              name="institution"
              value={formData.institution}
              onChange={onInputChange}
              disabled={!isEditing}
              className={!isEditing ? "bg-muted/50" : ""}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={onInputChange}
              disabled={!isEditing}
              className={!isEditing ? "bg-muted/50" : ""}
              rows={3}
            />
          </div>

          <Separator className="my-4" />
          <h3 className="text-lg font-medium mb-2">Social Links</h3>

          <div className="space-y-2">
            <Label htmlFor="website">Website URL</Label>
            <Input
              id="website"
              name="website"
              value={formData.website}
              onChange={onInputChange}
              disabled={!isEditing}
              className={!isEditing ? "bg-muted/50" : ""}
              placeholder={isEditing ? "https://yourwebsite.com" : "Not provided"}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="twitter">Twitter URL</Label>
            <Input
              id="twitter"
              name="twitter"
              value={formData.twitter}
              onChange={onInputChange}
              disabled={!isEditing}
              className={!isEditing ? "bg-muted/50" : ""}
              placeholder={isEditing ? "https://twitter.com/username" : "Not provided"}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="linkedin">LinkedIn URL</Label>
            <Input
              id="linkedin"
              name="linkedin"
              value={formData.linkedin}
              onChange={onInputChange}
              disabled={!isEditing}
              className={!isEditing ? "bg-muted/50" : ""}
              placeholder={isEditing ? "https://linkedin.com/in/username" : "Not provided"}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="orcid">ORCID URL</Label>
            <Input
              id="orcid"
              name="orcid"
              value={formData.orcid}
              onChange={onInputChange}
              disabled={!isEditing}
              className={!isEditing ? "bg-muted/50" : ""}
              placeholder={isEditing ? "https://orcid.org/0000-0000-0000-0000" : "Not provided"}
            />
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        {isEditing ? (
          <>
            <Button variant="outline" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={onSignOut}>
              Sign Out
            </Button>
            <Button onClick={onEdit}>Edit Profile</Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
};

export default ProfileForm;
